import { useState, useRef } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSCheckBox from "views/system-design/DSCheckBox";
import DSIconButton from "views/system-design/DSIconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Ticket } from "../../types";
import { useTickets } from "../../context/TicketsContext";
import { generateId, useOptimisticState } from "../../utils";

interface TicketSubtasksProps {
  ticket: Ticket;
}

export default function TicketSubtasks({ ticket }: TicketSubtasksProps) {
  const {
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    updateSubtask,
    reorderSubtasks,
  } = useTickets();
  const [localSubtasks, setLocalSubtasks] = useOptimisticState(ticket.subtasks);
  const [newTitle, setNewTitle] = useState("");
  const newSubtaskInputRef = useRef<HTMLInputElement>(null);
  const [subtaskEditing, setSubtaskEditing] = useState<{
    subtaskId: string;
    draft: string;
  } | null>(null);

  const completedCount = localSubtasks.filter(
    (subtask) => subtask.completed,
  ).length;
  const total = localSubtasks.length;
  const progress = total > 0 ? (completedCount / total) * 100 : 0;

  const handleAdd = () => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;

    addSubtask(ticket.id, {
      id: generateId(),
      title: trimmed,
      completed: false,
    });
    setNewTitle("");
    setTimeout(() => newSubtaskInputRef.current?.focus(), 0);
  };

  const handleToggle = (subtaskId: string) => {
    toggleSubtask(ticket.id, subtaskId);
  };

  const handleDelete = (subtaskId: string) => {
    deleteSubtask(ticket.id, subtaskId);
  };

  const handleStartEdit = (subtaskId: string, currentTitle: string) => {
    setSubtaskEditing({ subtaskId, draft: currentTitle });
  };

  const handleSaveEdit = () => {
    if (!subtaskEditing) return;
    const trimmed = subtaskEditing.draft.trim();
    if (
      trimmed &&
      trimmed !==
        localSubtasks.find((subtask) => subtask.id === subtaskEditing.subtaskId)
          ?.title
    ) {
      updateSubtask(ticket.id, subtaskEditing.subtaskId, { title: trimmed });
    }
    setSubtaskEditing(null);
  };

  const handleCancelEdit = () => {
    setSubtaskEditing(null);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reordered = Array.from(localSubtasks);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setLocalSubtasks(reordered);
    reorderSubtasks(
      ticket.id,
      reordered.map((subtask) => subtask.id),
    );
  };

  return (
    <DSBox sx={{ mt: 3 }}>
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <DSTypography variant="subtitle2" sx={{ color: "text.secondary" }}>
          Subtasks
        </DSTypography>
        {total > 0 && (
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            {completedCount}/{total}
          </DSTypography>
        )}
      </DSBox>

      {total > 0 && (
        <DSBox
          sx={{
            height: 4,
            bgcolor: "white.12",
            borderRadius: 2,
            mb: 1.5,
            overflow: "hidden",
          }}
        >
          <DSBox
            sx={{
              height: "100%",
              width: `${progress}%`,
              bgcolor: progress === 100 ? "success.main" : "primary.main",
              borderRadius: 2,
              transition: "width 0.3s",
            }}
          />
        </DSBox>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="subtasks">
          {(droppableProvided) => (
            <DSBox
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              {localSubtasks.map((subtask, index) => (
                <Draggable
                  key={subtask.id}
                  draggableId={subtask.id}
                  index={index}
                >
                  {(draggableProvided, draggableSnapshot) => (
                    <DSBox
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        borderRadius: 1,
                        "&:hover .drag-handle": { opacity: 1 },
                        "&:hover .delete-btn": { opacity: 1 },
                        ...(draggableSnapshot.isDragging && {
                          bgcolor: "action.hover",
                          boxShadow: 2,
                        }),
                      }}
                    >
                      <DSBox
                        {...draggableProvided.dragHandleProps}
                        className="drag-handle"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          cursor: "grab",
                          color: "text.secondary",
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 16 }} />
                      </DSBox>
                      <DSCheckBox
                        checked={subtask.completed}
                        onChange={() => handleToggle(subtask.id)}
                        size="small"
                      />
                      {subtaskEditing?.subtaskId === subtask.id ? (
                        <DSTextField
                          value={subtaskEditing.draft}
                          onChange={(event) =>
                            setSubtaskEditing((prev) =>
                              prev
                                ? { ...prev, draft: event.target.value }
                                : prev,
                            )
                          }
                          onKeyDown={(event) => {
                            if (
                              event.key === "Enter" &&
                              (event.metaKey || event.altKey)
                            ) {
                              event.preventDefault();
                              handleSaveEdit();
                            } else if (event.key === "Escape") {
                              handleCancelEdit();
                            }
                          }}
                          onBlur={handleSaveEdit}
                          size="small"
                          fullWidth
                          multiline
                          maxRows={4}
                          autoFocus
                          sx={{ flex: 1 }}
                        />
                      ) : (
                        <DSTypography
                          variant="body2"
                          onClick={() =>
                            handleStartEdit(subtask.id, subtask.title)
                          }
                          sx={{
                            flex: 1,
                            textDecoration: subtask.completed
                              ? "line-through"
                              : "none",
                            color: subtask.completed
                              ? "text.secondary"
                              : "text.primary",
                            cursor: "text",
                            borderRadius: 0.5,
                            px: 0.5,
                            whiteSpace: "pre-wrap",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          {subtask.title}
                        </DSTypography>
                      )}
                      <DSIconButton
                        className="delete-btn"
                        size="small"
                        onClick={() => handleDelete(subtask.id)}
                        sx={{ opacity: 0, transition: "opacity 0.2s" }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </DSIconButton>
                    </DSBox>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </DSBox>
          )}
        </Droppable>
      </DragDropContext>

      <DSBox
        sx={{ display: "flex", gap: 0.5, mt: 1, alignItems: "flex-start" }}
      >
        <DSTextField
          inputRef={newSubtaskInputRef}
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
          placeholder={`Add a subtask... (${typeof navigator !== "undefined" && navigator.platform.includes("Mac") ? "⌘" : "Alt"}+Enter to add)`}
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.altKey)) {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <DSIconButton size="small" onClick={handleAdd} sx={{ mt: 0.5 }}>
          <AddIcon fontSize="small" />
        </DSIconButton>
      </DSBox>
    </DSBox>
  );
}
